import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import AsyncSelect from 'react-select/async';
import Avatar from 'react-avatar';

import './ManagerAutocompleteSelect.scss';
import { fetchOrgChart } from '../../../store/apis/dashboardService';
import { getAuthUser } from '../../../utils/auth.util';
import { ImageFileUtil } from '../../../utils';

function ManagerAutocompleteSelect({
  mappedFn,
  onChange,
  value,
  inputIcon = null,
  nameAssessor = 'name',
  userCodeAssessor = 'code',
  designationAssessor = 'designation',
  imageAssessor = 'image',
  rightKeyAssessor = null,
  defaultShowMenu = false,
  defaultMenu = false,
  defaultInput = false,
  customRightIcon = null,
  width,
  ...props
}) {
  const [managerData, setManagerData] = useState([]);

  const { data: managers = [] } = useQuery({
    queryKey: ['fetchedChart'],
    queryFn: fetchOrgChart,
  });

  useEffect(() => {
    if (managers && managers?.length > 0) {
      const normalize = str => str?.trim().toLowerCase();
      const { employeeId } = getAuthUser();
      const userCode = normalize(employeeId);
      const self = managers.find(emp => normalize(emp.EmpCd) === userCode);
      const allDirectReports =
        managers.filter(
          emp => normalize(emp.DirectMgrName) === normalize(self.EmpName),
        ) || [];
      const root = [{ ...self }, ...allDirectReports];
      const data = root.map(item => ({
        value: item?.EmpCd?.trim(),
        label: item?.EmpName?.trim(),
        name: item?.EmpName?.trim(),
        code: item?.EmpCd?.trim(),
        designation: item?.Designation,
        image: item?.Imagefile,
      }));
      setManagerData(data);
    }
  }, [managers]);

  const menuPortalTarget =
    typeof document !== 'undefined' ? document.body : null;

  const formatOptionLabel = (option, { context }) => {
    if (context === 'value' && !defaultInput) {
      return (
        <div className="d-flex align-items-center gap-1">
          <div className="d-flex align-items-center gap-1">
            {inputIcon && inputIcon}
            <span className="fw-semibold">{option.label}</span>{' '}
          </div>
          <span style={{ color: option.color || '#475467' }}>
            {rightKeyAssessor &&
              option?.[rightKeyAssessor]?.toUpperCase() &&
              '#'}
            {rightKeyAssessor
              ? option?.[rightKeyAssessor]?.toUpperCase()
              : null}
          </span>
        </div>
      );
    }

    const getImage = () => {
      if (option?.[imageAssessor]) {
        return ImageFileUtil.getEmployeeFilePath(option?.[imageAssessor]);
      }
      return null;
    };

    if (!defaultMenu) {
      return (
        <div className="user-option">
          <Avatar
            name={option?.[nameAssessor] || '-'}
            round
            size="48px"
            color="#CFCBDC"
            fgColor="#475467"
            className="user-option__avatar"
            maxInitials={2}
            style={{ fontSize: '14px', fontWeight: 600 }}
            src={getImage()}
          />

          <div className="user-option__main">
            <span className="user-option__code">
              {option?.[userCodeAssessor]?.toUpperCase() || 'AE00000'}
            </span>
            <span className="user-option__name">
              {option?.[nameAssessor] || '-'}
            </span>
          </div>

          <span className="user-option__title">
            {option?.[designationAssessor] || ''}
          </span>
        </div>
      );
    }

    return option?.label;
  };

  const tooltipText =
    value && !defaultInput
      ? `${value.label}${rightKeyAssessor ? ` #${value[rightKeyAssessor] ?? value.value}` : ''}`
      : props.tooltipTitle || 'Search...';

  const selectStyles = {
    option: (provided, state) => ({
      ...provided,
      color: state.isSelected ? '#ffffff' : provided.color,
      cursor: 'pointer',
    }),
    menuPortal: base => ({ ...base, zIndex: 9999 }),
    ...props.styles,
    menu: base => ({
      ...base,
      width,
    }),
    menuList: base => ({
      ...base,
      width,
    }),
    control: base => ({
      ...base,
      border: '1px solid #ced4da',
      borderRadius: '8px',
      backgroundColor: props.isDisabled ? '#EFF2F7' : base.backgroundColor,
    }),
    container: base => ({
      ...base,
      width: props.inputWidth,
      height: props.inputHeight,
    }),
    valueContainer: base => ({
      ...base,
      cursor: 'text',
      padding: '0.255rem 0.75rem',
    }),
  };

  // Custom Components for React Select
  const customComponents = {
    ...(customRightIcon && {
      DropdownIndicator: () => customRightIcon,
      IndicatorSeparator: () => null,
    }),
  };

  return (
    <span title={tooltipText} className="manager-autocomplete">
      <AsyncSelect
        cacheOptions
        defaultOptions={defaultShowMenu ? managerData : []}
        loadOptions={async inputValue => {
          if (!inputValue && !defaultShowMenu) return [];
          const filteredData = managerData?.filter(manager => {
            const searchValue = inputValue.toLowerCase();
            return (
              (manager?.name &&
                manager.name.toLowerCase().includes(searchValue)) ||
              (manager?.code &&
                manager.code.toLowerCase().includes(searchValue)) ||
              (manager?.designation &&
                manager.designation.toLowerCase().includes(searchValue))
            );
          });

          return mappedFn ? mappedFn(filteredData) : filteredData;
        }}
        onChange={onChange}
        className={`basic-single manager-select ${props.className || ''}`}
        classNamePrefix="select"
        isClearable={props.isClearable ?? true}
        isSearchable={props.isSearchable ?? true}
        placeholder={props.placeholder || 'Search...'}
        value={value}
        menuPortalTarget={menuPortalTarget}
        styles={selectStyles}
        formatOptionLabel={formatOptionLabel}
        isDisabled={managerData.length === 1 || props.isDisabled}
        components={customComponents}
        noOptionsMessage={() => null}
      />
    </span>
  );
}

export default ManagerAutocompleteSelect;
